
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import App from './App';
import './index.css';
import { Toaster } from "@/components/ui/toaster";
import { supabase } from "@/integrations/supabase/client";
import { clearImageUrlCache } from '@/utils/imageUtils';
import { SessionContextProvider } from '@supabase/auth-helpers-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { logger } from '@/utils/logger';

// Create a query client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Initialize app services after render to avoid blocking initial load
const initializeAppServices = () => {
  // Run storage initialization after a brief delay to not block rendering
  setTimeout(async () => {
    try {
      logger.debug("Checking product-images storage bucket...");
      
      const { data: buckets, error: listError } = await supabase.storage.listBuckets();
      
      if (listError) {
        logger.warn("Could not list storage buckets, continuing with fallback images:", listError);
        return;
      }
      
      const productBucket = buckets?.find(b => b.name === 'product-images');
      
      if (!productBucket) {
        logger.warn("Product images bucket not found, using fallback image handling");
        return;
      }
      
      logger.debug("✅ Product images bucket found");
      
      try {
        const { data: files, error: filesError } = await supabase.storage
          .from('product-images')
          .list('', { limit: 1 });
          
        if (filesError) {
          logger.warn("Storage access issues detected, using fallback images:", filesError);
        } else {
          logger.debug(`✅ Successfully accessed product-images bucket with ${files?.length || 0} files`);
        }
      } catch (accessError) {
        logger.warn("Storage access test failed, using fallback images:", accessError);
      }
      
      clearImageUrlCache();
    } catch (error) {
      logger.warn("Storage initialization failed, using fallback image paths:", error);
    }
  }, 100);
};

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <SessionContextProvider supabaseClient={supabase}>
        <BrowserRouter>
          <App />
          <Toaster />
        </BrowserRouter>
      </SessionContextProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

// Initialize services after app renders
initializeAppServices();
